'use client'

import { useParams, usePathname, useRouter } from 'next/navigation'
import { useSyncExternalStore } from 'react'

import { readLastEnvironment } from '@lib/last-team'
import { useTeam } from '@resolvers/teams'
import { isValidSlug } from '@server/orbit/helpers/slug'

export type EnvironmentKind = 'production' | 'sandbox'

export const ENVIRONMENT_KINDS: EnvironmentKind[] = ['sandbox', 'production']

export const DEFAULT_ENVIRONMENT: EnvironmentKind = 'sandbox'

export const isEnvironmentKind = (value: unknown): value is EnvironmentKind =>
  value === 'production' || value === 'sandbox'

const lastEnvironment = (): EnvironmentKind => {
  const stored = readLastEnvironment()
  return isEnvironmentKind(stored) ? stored : DEFAULT_ENVIRONMENT
}

export const landingPath = (teamSlug: string) => `/${teamSlug}/${lastEnvironment()}`

const subscribeToNothing = () => () => {}

export const useAppRoute = () => {
  const router = useRouter()
  const pathname = usePathname() ?? '/'
  const params = useParams<{ team?: string; env?: string }>()
  const teamParam = typeof params?.team === 'string' ? params.team : undefined
  const teamSlug = teamParam && isValidSlug(teamParam) ? teamParam : undefined
  const envParam = typeof params?.env === 'string' ? params.env : undefined
  const envInvalid = envParam !== undefined && !isEnvironmentKind(envParam)
  const fallbackKind = useSyncExternalStore(subscribeToNothing, lastEnvironment, () => DEFAULT_ENVIRONMENT)
  const envKind: EnvironmentKind = isEnvironmentKind(envParam) ? envParam : fallbackKind

  const { data, teamLoading, teamError } = useTeam(teamSlug)
  const environment = envInvalid ? undefined : data?.environments.find((candidate) => candidate.kind === envKind)
  const scope = data && environment ? { teamId: data.team.id, envId: environment.id } : undefined

  const envPath = (path = '') => (teamSlug ? `/${teamSlug}/${envKind}${path}` : '/')
  const teamPath = (path = '') => (teamSlug ? `/${teamSlug}${path}` : '/')

  return {
    router,
    pathname,
    teamSlug,
    envKind,
    envInvalid,
    team: data?.team,
    teamLogo: data?.logo ?? null,
    role: data?.role,
    billing: data?.billing,
    environments: data?.environments ?? [],
    environment,
    scope,
    teamLoading,
    teamError,
    envPath,
    teamPath,
    isOwner: data?.role === 'owner',
    isProduction: envKind === 'production'
  }
}
